import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Bug, CheckCircle2, RotateCcw } from 'lucide-react';

export default function AdminBugReports() {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, reviewed: 0 });
  const [filter, setFilter] = useState('all'); // 'all' | 'new' | 'reviewed'
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await api.get(`/admin/bug-reports${params}`);
      setReports(res.data.reports || []);
      setCounts(res.data.counts || { total: 0, new: 0, reviewed: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load bug reports');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await api.patch(`/admin/bug-reports/${id}`, { status: newStatus });
      toast.success(newStatus === 'reviewed' ? 'Marked as reviewed' : 'Reopened');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setUpdatingId(null); }
  };

  const FilterPill = ({ value, label, count }) => (
    <button
      onClick={() => setFilter(value)}
      data-testid={`bug-filter-${value}`}
      className={`px-4 h-9 rounded-full text-[12px] font-bold uppercase tracking-[.08em] transition-colors ${
        filter === value
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
      style={{ border: '1.5px solid', fontFamily: "'Barlow Condensed', sans-serif" }}
    >
      {label} {count != null && <span className="opacity-70">({count})</span>}
    </button>
  );

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-bug-reports-page">
      <div className="flex items-center gap-2 mb-1">
        <Bug className="w-5 h-5 text-slate-700" />
        <h1 className="text-2xl font-extrabold uppercase tracking-tight text-slate-900"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Bug Reports</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        {counts.total} total · {counts.new} new · {counts.reviewed} reviewed
      </p>

      <div className="flex gap-2 mb-5">
        <FilterPill value="all" label="All" count={counts.total} />
        <FilterPill value="new" label="New" count={counts.new} />
        <FilterPill value="reviewed" label="Reviewed" count={counts.reviewed} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <p className="text-slate-500 font-medium">No bug reports {filter !== 'all' ? `with status "${filter}"` : 'yet'}.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="bug-reports-list">
          {reports.map(r => (
            <div key={r.report_id} className="bg-white rounded-xl border border-slate-200 p-4"
              data-testid={`bug-row-${r.report_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-slate-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {r.account_id || 'No Account ID'}
                    {r.user_email && <span className="text-slate-400 font-normal"> · {r.user_email}</span>}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[.08em] ${
                    r.status === 'reviewed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {r.status}
                </span>
              </div>
              <p className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap mb-3">
                {r.description}
              </p>
              <div className="flex items-center justify-end gap-2">
                {r.status === 'new' ? (
                  <button
                    onClick={() => updateStatus(r.report_id, 'reviewed')}
                    disabled={updatingId === r.report_id}
                    data-testid={`bug-mark-reviewed-${r.report_id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[.08em] bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Mark Reviewed
                  </button>
                ) : (
                  <>
                    {r.reviewed_by && (
                      <span className="text-[11px] text-slate-400 mr-2">
                        Reviewed by {r.reviewed_by}{r.reviewed_at ? ` · ${new Date(r.reviewed_at).toLocaleDateString()}` : ''}
                      </span>
                    )}
                    <button
                      onClick={() => updateStatus(r.report_id, 'new')}
                      disabled={updatingId === r.report_id}
                      data-testid={`bug-reopen-${r.report_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[.08em] bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Reopen
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
